import { useField, FieldProps } from 'formik';
import AlertCircleOutline from 'mdi-material-ui/AlertCircleOutline';
import Select, { components, GroupBase, Options, OptionProps } from 'react-select';

import { Tooltip } from '../Elements';

import './CustomSelect.css';

import { FieldWrapperPassThroughProps } from './FieldWrapper';
import { Option, filterValue } from './types';

interface GroupedOption {
	readonly label: string;
	readonly options: readonly Option[];
}

type CustomSelectPropsShared = {
	options: Options<Option>;
	isMulti?: boolean;
	disabled?: boolean;
	disabledHelpText?: string;
	className?: string;
	label: string | React.ReactElement;
	placeholder?: string;
	SelectTooltip?: typeof Tooltip;
	handleChange?: (newValue: string | string[]) => void;
	isOptional?: boolean;
	hideLabel?: boolean;
	customLabel?: (data: Option) => React.ReactElement;
	isLargeInput?: boolean;
	name?: string;
	field?: { name: string };
};

// Removed empty interface, use FieldProps directly

export type CustomSelectProps =
	| (FieldProps & CustomSelectPropsShared)
	| (CustomSelectPropsShared & FieldWrapperPassThroughProps);

export const CustomSelect = (props: CustomSelectProps) => {
	const {
		className,
		label,
		placeholder,
		field: fieldProp,
		options,
		isMulti = false,
		disabled = false,
		disabledHelpText,
		SelectTooltip = null,
		handleChange,
		isOptional = false,
		hideLabel = false,
		customLabel,
		isLargeInput = false,
	} = props;

	const name = fieldProp?.name || props.name || 'form-field';
	const [field, meta, helpers] = useField(name);

	// Create an array for the Select component's options.
	let selectOptions = options;

	// Add the 'None' option at the beginning if isOptional is true.
	if (isOptional) {
		selectOptions = [{ label: 'None', value: '' }, ...selectOptions];
	}

	const onChange = (option: readonly Option[] | Option | null) => {
		const newValue = isMulti
			? (option as Option[]).map((item: Option) => item.value)
			: (option as Option).value;
		helpers.setValue(newValue);
		// make sure value has actually changed before firing handleChange
		if (handleChange && getValue()?.value !== newValue) {
			handleChange(newValue);
		}
		setTimeout(() => {
			// fix an issue on mobile where blur event fires before onchange event
			helpers.setTouched(true);
		}, 100);
	};

	const checkValue = (option: Option, val: unknown) => {
		// check if the option matches the value considering it could be an object or just a plain value
		if (typeof val === 'object' && val !== null) {
			return Object.values(val).includes((option.value as unknown as filterValue).id);
		}
		return val === option.value;
	};

	const getValue = () => {
		if (options) {
			if (isMulti) {
				return options.filter(
					(option) =>
						field.value?.length >= 1 &&
						(field.value as filterValue[])?.some((elm: filterValue) =>
							checkValue(option, elm)
						)
				);
			} else {
				// check if options have groups
				if (options[0]?.options) {
					// does have groups, so use the group options to check for value
					return options.reduce((result: Option[], option) => {
						option?.options?.map((o) => {
							if (checkValue(o, field.value)) {
								result.push(o);
							}
						});
						return result;
					}, []);
				} else {
					return options.find((option) => field.value && checkValue(option, field.value));
				}
			}
		} else {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return isMulti ? [] : ('' as any);
		}
	};

	const Option = (props: OptionProps<unknown, boolean, GroupBase<unknown> & GroupedOption>) => {
		return isMulti ? (
			<components.Option {...props}>
				<input type='checkbox' checked={props.isSelected} onChange={() => null} />{' '}
				<label>{customLabel ? customLabel(props.data as Option) : props.label}</label>
			</components.Option>
		) : (
			<components.Option {...props}>{props.label}</components.Option>
		);
	};

	const dot = (color?: string) => {
		if (color) {
			return {
				':before': {
					backgroundColor: color,
					borderRadius: 6,
					content: '" "',
					display: 'inline-block',
					marginRight: 10,
					marginBottom: 2,
					height: 6,
					width: 6,
				},
			};
		} else {
			return {};
		}
	};

	const setTouched = () => {
		helpers.setTouched(true);
	};
	const { error, touched } = meta;

	const formatGroupLabel = (data: GroupedOption) => (
		<div className='font-semibold text-primaryTextDark normal-case text-base pb-2'>
			{data.label}
		</div>
	);

	return (
		<div data-test-id='custom-select'>
			<div className='flex items-center'>
				<label
					htmlFor={field.name}
					className={`block text-primaryTextDark ${hideLabel ? 'invisible' : ''} ${
						disabled ? 'opacity-25' : ''
					}`}
				>
					{label}
				</label>
				{SelectTooltip as React.ReactNode}
			</div>
			<Select
				className={className}
				name={field.name}
				inputId={field.name}
				value={getValue() || ''}
				// eslint-disable-next-line
				// @ts-ignore
				onChange={onChange}
				onBlur={setTouched}
				placeholder={placeholder}
				options={selectOptions}
				isOptionDisabled={(option) => !!(option as Option).disabled}
				isMulti={isMulti}
				isDisabled={disabled}
				closeMenuOnSelect={!isMulti}
				menuPortalTarget={document.body}
				components={{
					Option,
				}}
				classNames={{
					input: () => `custom-select__input-outer ${isLargeInput ? 'text-md' : ''}`,
					placeholder: () => (isLargeInput ? '!text-md' : ''),
					option: () => `!flex items-center gap-2 ${isLargeInput ? '!text-md' : ''}`,
					singleValue: () => (isLargeInput ? '!text-md' : ''),
				}}
				formatGroupLabel={formatGroupLabel}
				styles={{
					container: (base) => ({
						...base,
						marginTop: 4,
						borderColor:
							error && touched ? 'hsla(356, 93%, 47%, 1)' : 'hsla(240, 3%, 36%, 1)',
						borderRadius: 1,
					}),
					control: (base) => ({
						...base,
						paddingTop: 3,
						paddingBottom: 3,
						borderColor:
							error && touched ? 'hsla(356, 93%, 47%, 1)' : 'hsla(240, 3%, 36%, 1)',
					}),
					option: (base, { data }) => ({
						...base,
						fontSize: 14,
						...dot((data as Option).statusColor),
					}),
					singleValue: (base, { data }) =>
						disabled
							? {
									...base,
									fontSize: 14,
									color: 'hsla(240, 1%, 64%, 1)',
									...dot((data as Option).statusColor),
								}
							: {
									...base,
									fontSize: 14,
									color: 'hsla(240, 3%, 36%, 1)',
									...dot((data as Option).statusColor),
								},
					menuPortal: (base) => ({ ...base, zIndex: 9999 }),
					placeholder: (base) => ({
						...base,
						color: 'hsla(246, 50%, 4%, 1)',
						fontSize: 14,
					}),
				}}
			/>
			{touched && error && (
				<p className='mt-2 text-base text-error' id='select-error'>
					<AlertCircleOutline
						className='pr-2 mt-[-3px]'
						data-test-id='AlertCircleOutlineIcon'
					/>
					{error}
				</p>
			)}
			{disabled && disabledHelpText && (
				<p className='mt-2 text-base text-secondaryTextDark'>{disabledHelpText}</p>
			)}
		</div>
	);
};

export default CustomSelect;
